import type {CommitMessageGenerationConfig} from '@github-ui/diff-lines/conversation-types'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {pageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useQuery} from '@github-ui/react-query'
import {reactFetch} from '@github-ui/verified-fetch'
import {useOptionalLayoutCommitMessageGenerationConfig} from '../stores/LayoutStore'

type CommitMessageGenerationConfigResponse = {
  commitMessageGenerationConfig: CommitMessageGenerationConfig | null
}

/**
 * Resolves the PR-scoped `CommitMessageGenerationConfig`, preferring any inline/context value
 * already provided and otherwise fetching `page_data/commit_message_generation_config` on demand
 * when `enabled` is true.
 */
export function useCommitMessageGenerationConfig(options?: {
  enabled?: boolean
  basePath?: string
  initialConfig?: CommitMessageGenerationConfig | null
}): CommitMessageGenerationConfig | null | undefined {
  const contextConfig = useOptionalLayoutCommitMessageGenerationConfig()
  const inlineConfig = options?.initialConfig !== undefined ? options.initialConfig : contextConfig
  const basePath = options?.basePath
  const shouldFetch = Boolean(options?.enabled) && inlineConfig === undefined && Boolean(basePath)
  const apiURL = basePath ? pageDataUrl(basePath, PageData.commitMessageGenerationConfig) : ''

  const {data} = useQuery<CommitMessageGenerationConfigResponse | null>({
    queryKey: [PageData.commitMessageGenerationConfig, apiURL],
    queryFn: async () => {
      const result = await reactFetch(apiURL)
      if (result.status === 404) return {commitMessageGenerationConfig: null}
      if (!result.ok) throw new Error(`HTTP ${result.status} (${apiURL})`)
      return (await result.json()) as CommitMessageGenerationConfigResponse | null
    },
    enabled: shouldFetch,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  if (inlineConfig !== undefined) return inlineConfig
  return data === undefined ? undefined : (data?.commitMessageGenerationConfig ?? null)
}
